import { FC, ReactNode } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Caret } from '@zazume/zzm-base';

const DocumentContainer = styled.div(
  () => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  `
);

const NavigationButton = styled.button(
  ({ theme }) => css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background-color: ${theme.colors.White};
    border: 1px solid ${theme.colors.Gray300};
    cursor: pointer;
    z-index: 10;
    transition: all 0.2s ease;

    ${theme.mq.desktop} {
      display: none;
    }

    &:hover {
      background-color: ${theme.colors.Gray200};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px ${theme.colors.Primary}40;
    }
  `
);

const PreviousButton = styled(NavigationButton)`
  left: ${({ theme }) => theme.spacing.s4};
`;

const NextButton = styled(NavigationButton)`
  right: ${({ theme }) => theme.spacing.s4};
`;

interface Props {
  documentId: string;
  onNextClick?: (id: string) => void;
  onPreviousClick?: (id: string) => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
  children: ReactNode;
}

export const DocumentWithControlsContainer: FC<Props> = ({
  onNextClick,
  onPreviousClick,
  hasNext,
  hasPrevious,
  children,
  documentId
}) => {
  const showNavigation = Boolean(onNextClick || onPreviousClick);
  const canGoNext = hasNext !== undefined ? hasNext : Boolean(onNextClick);
  const canGoPrevious = hasPrevious !== undefined ? hasPrevious : Boolean(onPreviousClick);

  return (
    <DocumentContainer>
      {showNavigation && onPreviousClick && (
        <PreviousButton onClick={() => onPreviousClick(documentId)} type="button" disabled={!canGoPrevious}>
          <Caret direction="left" size={24} color="Gray600" />
        </PreviousButton>
      )}
      {children}
      {showNavigation && onNextClick && (
        <NextButton onClick={() => onNextClick(documentId)} type="button" disabled={!canGoNext}>
          <Caret direction="right" size={24} color="Gray600" />
        </NextButton>
      )}
    </DocumentContainer>
  );
};
